import React, { useState } from "react";

import { useStandardProductContext } from "app/providers/StandardProductProvider";
import { CategoryStandardProduct } from "shared/api/middleware-api-client";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
import { PANEL_SET_CODES } from "shared/utils/standard-product";
import { useAnalytics } from "../analytics/useAnalytics";

export interface StandardProductFooterDataResultProps {
  isMonthlyPaymentModalOpened: boolean;
  setIsMonthlyPaymentModalOpened: React.Dispatch<React.SetStateAction<boolean>>;
  showMonthlyPayment: boolean;
  standardProduct: CategoryStandardProduct;
  standardProductName: string;
  standardProductPrice: string;
  changeAmount: (amount: number) => void;
  amount: number;
}

export const useStandardProductFooterData = (
  monthlyPayment?: number,
): Partial<StandardProductFooterDataResultProps> => {
  // Access the standard product store and actions from the StandardProductProvider
  const { store: standardProductStore, actions } = useStandardProductContext();

  const { onStandardProductConfig } = useAnalytics();

  // State for controlling the visibility of the monthly payment modal
  const [isMonthlyPaymentModalOpened, setIsMonthlyPaymentModalOpened] =
    useState(false);

  // If no standard product is selected, return an empty object
  if (!standardProductStore.selectedStandardProduct) return {};

  // Determine if the monthly payment should be shown based on the provided value
  const showMonthlyPayment = !!monthlyPayment && monthlyPayment > 0;

  // Get the selected standard product
  const standardProduct = standardProductStore.selectedStandardProduct;

  // Get the name of the selected standard product
  const standardProductName = standardProductStore.selectedStandardProduct.name;

  // Calculate the formatted price of the selected standard product
  const standardProductPrice = getFormattedPrice(
    standardProductStore.selectedStandardProduct.price *
      (standardProductStore.selectedStandardProduct.amount || 1),
  );

  // Function for changing the amount of the selected standard product
  const changeAmount = (amount: number) => {
    if (standardProductStore.selectedStandardProduct) {
      const product = {
        ...standardProductStore.selectedStandardProduct,
        amount: Math.max(
          (standardProductStore.selectedStandardProduct.amount || 0) + amount,
          1,
        ),
      };

      actions.selectStandardProduct(product);
      onStandardProductConfig(product);
    }
  };

  // Get the current amount of the selected standard product
  let amount = standardProductStore.selectedStandardProduct.amount;

  // If selected standard product is a set, then multiply amount by 2
  if (
    PANEL_SET_CODES.includes(standardProductStore.selectedStandardProduct.code)
  ) {
    amount *= 2;
  }

  return {
    isMonthlyPaymentModalOpened,
    setIsMonthlyPaymentModalOpened,
    showMonthlyPayment,
    standardProduct,
    standardProductName,
    standardProductPrice,
    changeAmount,
    amount,
  };
};
